import { useState, useRef, useEffect } from "react";
import { ToolGroupManager, annotation } from "@cornerstonejs/tools";
import { ReportingToolConfig } from "../tools/ReportingTool";
import "../../styles/viewerToolbar.css";
import { StackScrollToolConfig } from "../tools/StackScrollTool";
import { ZoomToolConfig } from "../tools/ZoomTool";
import { PanToolConfig } from "../tools/PanTool";
import { WindowLevelToolConfig } from "../tools/WindowLevelTool";
import { ProbeToolConfig } from "../tools/ProbeTool";
import { MPRToolConfig } from "../tools/MPRTool";
import { ArrowAnnotateToolConfig } from "../tools/AnnotationTool";
import { RotateToolConfig } from "../tools/RotateTool";
import { DeleteToolConfig } from "../tools/DeleteTool";
import { DrawToolConfig } from "../tools/DrawTool";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { SeriesToolConfig } from "../tools/SeriesTool";
import { SyncScrollToolConfig } from "../tools/SyncScrollTool";
import { DownloadZipToolConfig } from "../tools/DownloadZipTool";
import { HistoryPatientToolConfig, openHistoryPatient } from "../tools/HistoryPatient";
import { AttachedImageToolConfig } from "../tools/AttechedImageTool";
import { CompareStudyToolConfig } from "../tools/CompareStudyTool";
import { AddTagsToolConfig } from "../tools/AddTagTool";
import { LayoutToolConfig } from "../tools/LayoutTool";
import { ContrastToolConfig } from "../tools/ContrastTool";
import LayoutPicker from "./LayoutPicker";
import SpeakReportPopup, { SpeakReportToolConfig } from "../tools/SpeakReportTool";
import AIReportToolConfig from "../tools/AIReportingTool";
import { WindowLevelTool } from "@cornerstonejs/tools";
import WordReportPopup, { WordReportToolConfig } from "../tools/WordReportingTool";

export default function ViewerToolbar({
  onToggleSeries,
  onLayoutChange,
  onMPRViewChange,
  onSyncToggle,
  isSyncScroll,
  onAddTags,
  onCompareStudy,
  onDownloadZip,
  onToolChange,
  currentActiveTool,
  setActiveTool: setActiveToolProp,
  currentPatient,
}) {
  const [activeTool, setActiveTool] = useState(currentActiveTool || "StackScrollTool");
  const [showSpeakReport, setShowSpeakReport] = useState(false);
  const [showWordReport, setShowWordReport] = useState(false);
  // ── REF FOR ARROW SCROLLING ──────────────────────────────────────────────
  const toolbarRef = useRef(null);
  // Check if the current scan is an X-Ray modality (e.g., DX, CR, XR)
  const isXray = ["DX", "CR", "XR"].includes(currentPatient?.modality?.toUpperCase());
  const scrollToolbar = (direction) => {
    if (toolbarRef.current) {
      const scrollAmount = direction === "left" ? -240 : 240;
      toolbarRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleSetActiveTool = (toolName) => {
    setActiveTool(toolName);
    if (onToolChange) onToolChange(toolName);
    if (setActiveToolProp) setActiveToolProp(toolName);
  };
  const tools = [
    StackScrollToolConfig,
    ZoomToolConfig,
    PanToolConfig,
    ContrastToolConfig,
    LayoutToolConfig,
    WindowLevelToolConfig,
    RotateToolConfig,
    MPRToolConfig(onMPRViewChange, handleSetActiveTool),
    //SyncScrollToolConfig(onSyncToggle, isSyncScroll),
    ArrowAnnotateToolConfig,
    DeleteToolConfig,
    ProbeToolConfig,
    DrawToolConfig,
    SeriesToolConfig,
    DownloadZipToolConfig(onDownloadZip),
  //  ReportingToolConfig,
    // {
    //   ...SpeakReportToolConfig,
    //   customAction: () => setShowSpeakReport(true),
    // },
    {
      ...WordReportToolConfig,
      customAction: () => setShowWordReport(true),
    },
    // {
    //   ...AIReportToolConfig,
    //   customAction: () => {
    //     const resolvedStudyId =
    //       currentPatient?.study_id ||
    //       currentPatient?.studyId ||
    //       currentPatient?.StudyInstanceUID ||
    //       currentPatient?.studies?.[0]?.study_id ||
    //       currentPatient?.studies?.[0]?.studyId ||
    //       currentPatient?.studies?.[0]?.StudyInstanceUID ||
    //       null;
    //     AIReportToolConfig.customAction(resolvedStudyId, null, null, currentPatient);
    //   },
    // },
    {
      ...HistoryPatientToolConfig,
      customAction: () => {
        openHistoryPatient({
          patient_id:
            currentPatient?.patient_id ||
            currentPatient?.patientId ||
            currentPatient?.id ||
            currentPatient?.PatientID ||
            currentPatient?.patientID ||
            "",
          patient_name:
            currentPatient?.patient_name ||
            currentPatient?.patient ||
            currentPatient?.name ||
            currentPatient?.PatientName ||
            "",
        });
      },
    },
   // AttachedImageToolConfig,
    //CompareStudyToolConfig(onCompareStudy),
    //AddTagsToolConfig(onAddTags),
  ];

  const ALL_TOOLS = tools
  .filter((t) => !t.customAction)
  .flatMap((t) => {
    if (t.children) {
      return t.children.map((child) => child.tool);
    }
    return t.tool;
  });

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const [layoutPickerPos, setLayoutPickerPos] = useState({ top: 0, left: 0 });

  const activateTool = (toolName) => {
    const toolGroups = ToolGroupManager.getAllToolGroups();
    if (!toolGroups.length) {
      console.warn("No toolGroups found");
      handleSetActiveTool(toolName);
      return;
    }
    toolGroups.forEach((toolGroup) => {
      ALL_TOOLS.forEach((tool) => {
        try {
          toolGroup.setToolPassive(tool);
        } catch (e) {}
      });

      toolGroup.setToolActive(toolName, {
        bindings: [{ mouseButton: 1 }],
      });

      if (toolName === "SeriesToggle") {
        onToggleSeries?.();
        return;
      }
      if (toolName === "DeleteTool") {
        annotation.state.removeAllAnnotations();
        handleSetActiveTool(toolName);
        return;
      }
      toolGroup.setToolActive(toolName, {
        bindings: [{ mouseButton: 1 }],
      });
    });
    handleSetActiveTool(toolName);
  };

  const toggleDropdown = (toolName, event) => {
    event.stopPropagation();
    if (activeDropdown === toolName) {
      setActiveDropdown(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    setActiveDropdown(toolName);
  };

  const handleToolClick = (tool, e) => {
    const isMPR = tool.tool === "MPRTool" || tool.name === "MPR"; // adjust based on MPRToolConfig
    console.log('setActivetool',setActiveTool)
    if (isMPR && isXray) {
      e.stopPropagation();
      return;
    }
    if (tool.isLayoutPicker) {
      handleSetActiveTool("LayoutTool");
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      setLayoutPickerPos({
        top: rect.bottom + 4,
        left: rect.left,
      });
      setShowLayoutPicker((prev) => !prev);
      return;
    }
    if (tool.customAction) {
      tool.customAction();
      return;
  }
  
  if (tool.children) {
      return;
  }
  
  activateTool(tool.tool);
  };

  const isToolActive = (tool) => {
    if (tool.tool === activeTool) return true;
    if (tool.children) return tool.children.some((c) => c.tool === activeTool);
    return false;
  };
  useEffect(() => {
    setActiveTool(currentActiveTool || "StackScrollTool");
  }, [currentActiveTool]);
  return (
    <>
      {/* ── TOOLBAR WRAPPER WITH NAV ARROWS ───────────────────────────────── */}
      <div className="viewerToolbarWrapper">
        {/* <button
          className="scrollNavBtn left"
          onClick={() => scrollToolbar("left")}
          title="Scroll Left"
        >
          ‹
        </button> */}

        <div
          className="viewerToolbar"
          ref={toolbarRef}
          onClick={() => setActiveDropdown(null)}
        >
          {tools.map((tool, index) => {
            const hasChildren = tool.children && tool.children.length > 0;
            const isMPR = tool.tool === "MPRTool" || tool.name === "MPR"; 
            const isDisabled = isMPR && isXray;
            return (
              <div
                className={`toolItem ${isToolActive(tool) ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                key={index}
                style={isDisabled ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" } : {}}
                title={isDisabled ? "MPR is not available for X-ray images" : ""}
                onClick={(e) => !isDisabled && handleToolClick(tool, e)}
              >
                {/* Top row container for Icon + Dropdown Arrow */}
                <div className="toolIconRow">
                  <div className="toolIcon">{tool.icon}</div>

                  {hasChildren && (
                    <div
                      className="dropdownArrow"
                      onClick={(e) => toggleDropdown(tool.tool, e)}
                    >
                      <ArrowDropDownIcon fontSize="small" />
                    </div>
                  )}
                </div>

                {/* Label stays centered directly underneath */}
                <div className="toolLabel">{tool.name}</div>
              </div>
            );
          })}
        </div>

        {/* <button
          className="scrollNavBtn right"
          onClick={() => scrollToolbar("right")}
          title="Scroll Right"
        >
          ›
        </button> */}
      </div>

      {activeDropdown &&
        (() => {
          const tool = tools.find((t) => t.tool === activeDropdown);
          if (!tool?.children) return null;
          return (
            <div
              className="presetDropdown"
              style={{ top: dropdownPos.top, left: dropdownPos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {tool.children.map((child, childIndex) => (
                <div
                  key={childIndex}
                  className="presetOption"
                  onClick={() => {
                    console.log("Clicked:", child.tool);
                
                    if (child.customAction) {
                        child.customAction();
                    } else {
                        activateTool(child.tool);
                    }
                
                    setActiveDropdown(null);
                }}
                >
                  {child.icon && (
                    <span className="presetChildIcon">{child.icon}</span>
                  )}
                  <div className="presetName">{child.name}</div>
                  {child.description && (
                    <div className="presetValues">{child.description}</div>
                  )}
                </div>
              ))}
            </div>
          );
        })()}

      {showLayoutPicker && (
        <LayoutPicker
          pos={layoutPickerPos}
          onSelect={(rows, cols) => onLayoutChange?.(rows, cols)}
          onClose={() => setShowLayoutPicker(false)}
        />
      )}

      {showSpeakReport && (
        <SpeakReportPopup
          patient={currentPatient}
          onClose={() => setShowSpeakReport(false)}
        />
      )}

      {showWordReport && (
        <WordReportPopup
          patient={currentPatient}
          onClose={() => setShowWordReport(false)}
        />
      )}
    </>
  );
}